import React, { ComponentProps } from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import { IconButton } from '@adsk/alloy-react-button';
import { TriangleDownIcon, TriangleUpIcon } from '@adsk/alloy-react-icon';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

type OnClickType = (
  event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
) => void;

type IncrementerProps = StylableComponent<HTMLDivElement> & {
  /** @deprecated */
  disabled?: boolean;
  disabledUp?: boolean;
  disabledDown?: boolean;
  onClickUp?: OnClickType;
  onClickDown?: OnClickType;
};

const ShortIconButton = styledC(IconButton)`
  height: 18px;
`;

const renderShortTriangleUpIcon = (
  props: ComponentProps<typeof TriangleUpIcon>,
) => <TriangleUpIcon viewBox={'0 0 24 17'} {...props} />;

const renderShortTriangleDownIcon = (
  props: ComponentProps<typeof TriangleDownIcon>,
) => <TriangleDownIcon viewBox={'0 5 24 17'} {...props} />;

/**
 * An Incrementer
 */
const Incrementer = React.forwardRef<HTMLDivElement, IncrementerProps>(
  (
    {
      className,
      style,
      disabled = false,
      disabledUp = false,
      disabledDown = false,
      onClickUp,
      onClickDown,
      ...props
    },
    ref,
  ) => {
    // Keeping general `disabled` prop until we release a breaking change
    const isDisabledUp = disabled || disabledUp;
    const isDisabledDown = disabled || disabledDown;

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            flexDirection: 'column',
            height: 36,
            marginTop: -1,
          },
          style,
        ]}
      >
        <ShortIconButton
          data-testid={'increment'}
          disabled={isDisabledUp}
          onClick={onClickUp}
          renderIcon={renderShortTriangleUpIcon}
        />
        <ShortIconButton
          data-testid={'decrement'}
          disabled={isDisabledDown}
          onClick={onClickDown}
          renderIcon={renderShortTriangleDownIcon}
        />
      </div>
    );
  },
);

Incrementer.displayName = 'Incrementer';

Incrementer.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Deprecated: toggle disabled state for both arrows */
  disabled: PropTypes.bool,
  /** Toggle disabled state for down arrow */
  disabledDown: PropTypes.bool,
  /** Toggle disabled state for up arrow */
  disabledUp: PropTypes.bool,
  /** Function executed upon clicking the up arrow */
  onClickUp: PropTypes.func.isRequired,
  /** Function executed upon clicking the down arrow */
  onClickDown: PropTypes.func.isRequired,
};

export default Incrementer;
